/**
 * MODEL formats the Needle ENGINE can load — the single inventory the editor
 * trusts (Marcel 2026-07-31: "our asset loading needs to know that needle
 * engine CAN load FBX, OBJ, SOG, PLY, RAD formats"). Source of truth is the
 * engine's loader wiring:
 * - `engine_loaders.ts` onCreateLoader: glTF/glb/vrm (GLTFLoader), fbx, obj
 * - `engine_loaders.custom.ts`: the Spark splat family — ply, spz, splat,
 *   ksplat, sog, rad
 * - explicitly NOT loadable: usd/usda/usdz ("not supported" in the engine),
 *   stl (no loader registered)
 * Keep in sync with the engine when loaders are added.
 */

/** glTF-family formats — preferred whenever a choice exists (richest fidelity:
 * components, extensions, progressive variants) */
export const GLTF_FAMILY_FORMATS: ReadonlySet<string> = new Set(["gltf", "glb", "vrm"]);

/** every extension the engine's model loaders accept (lowercase, no dot) */
export const ENGINE_MODEL_EXTENSIONS: ReadonlySet<string> = new Set([
    "gltf", "glb", "vrm",
    "fbx", "obj",
    "ply", "spz", "splat", "ksplat", "sog", "rad",
]);

/** the matching mimetypes (`tryDetermineMimetypeFromURL` vocabulary) for
 * probing extension-less urls */
export const ENGINE_MODEL_MIMETYPES: ReadonlySet<string> = new Set([
    "model/gltf+json", "model/gltf-binary", "model/vrm",
    "model/fbx", "model/vnd.autodesk.fbx", "model/obj",
    "model/ply", "model/spz", "model/splat", "model/ksplat", "model/sog", "model/rad",
]);

/** extension of a path/url (query stripped, lowercase, no dot) — "" when none */
export function modelExtensionOf(pathOrUrl: string): string {
    const path = pathOrUrl.split("?")[0];
    const name = path.split("/").pop() ?? "";
    const dot = name.lastIndexOf(".");
    return dot > 0 ? name.substring(dot + 1).toLowerCase() : "";
}

export function isEngineLoadableModelUrl(pathOrUrl: string): boolean {
    return ENGINE_MODEL_EXTENSIONS.has(modelExtensionOf(pathOrUrl));
}

export function isGltfFamilyUrl(pathOrUrl: string): boolean {
    return GLTF_FAMILY_FORMATS.has(modelExtensionOf(pathOrUrl));
}

/** needle-cloud `content_format` values the engine can load — the cloud's
 * format names match extension names, so ONE set answers both (glTF family,
 * fbx, obj, and the declared splat formats: ply/spz/splat/ksplat/sog/rad) */
export function isEngineLoadableCloudFormat(format: string | null | undefined): boolean {
    return !!format && ENGINE_MODEL_EXTENSIONS.has(format);
}

/** cloud formats the engine definitely CANNOT load — the only hard rejects */
const UNLOADABLE_CLOUD_FORMATS: ReadonlySet<string> = new Set([
    "usd", "usda", "usdz", "stl", "textures", "html",
    // hdr-family versions belong to the HDRI branch, never the model list
    "hdr", "exr", "pmrem",
]);

/**
 * Is a cloud VERSION model-loadable? Known-good formats pass (incl. declared
 * splat formats), known-bad reject — and UNKNOWN passes: OLD uploads predate
 * format stamping (null content_format, extension-less "…/file" urls), and the
 * ENGINE sniffs the actual content at load (e.g. PLY by header magic).
 * pickAssetVersion's glTF preference keeps unknowns from outranking a glb.
 */
export function isEngineLoadableCloudVersion(format: string | null | undefined, url: string | null | undefined): boolean {
    if (isEngineLoadableCloudFormat(format)) return true;
    if (format && UNLOADABLE_CLOUD_FORMATS.has(format)) return false;
    if (!url) return false;
    const extension = modelExtensionOf(url);
    if (extension.length === 0) return true; // extension-less cloud url — engine sniffs
    return ENGINE_MODEL_EXTENSIONS.has(extension);
}
